import { pageAssistantHandoffStore } from '@Ai/stores/pageAssistantHandoffStore';
import { setThemeOverride } from '@Core/hooks/useTheme';
import { isInsideEditorPreviewFrame } from '@Core/lib/pageEditorPreview';
import { router } from '@inertiajs/react';
import type { PageRenderer } from '@Pages/pageRuntime/PageRenderer';
import { useEffect, useEffectEvent, useRef, useState } from 'react';

import { randomUuid } from '@/lib/randomUuid';

import {
  EDITOR_PREVIEW_MESSAGE_TYPE,
  isEditorPreviewMessage,
  type EditorPreviewChildMessagePayload,
  type EditorPreviewParentMessage,
  type EditorPreviewViewer
} from './editorPreviewBridge';
import { routeParamsKey } from './previewLocation';

type PageDesignModeProp = NonNullable<React.ComponentProps<typeof PageRenderer>['designMode']>;

function sendToEditor(pageId: string, message: EditorPreviewChildMessagePayload) {
  if (!isInsideEditorPreviewFrame()) {
    return;
  }

  window.parent.postMessage(
    { type: EDITOR_PREVIEW_MESSAGE_TYPE, pageId, ...message },
    window.location.origin
  );
}

/**
 * Child side of the editor preview bridge, mounted by `ShowPage`. Inert on
 * a normal visit: it only activates inside the editor's named preview iframe
 * (so public embeds never see editor traffic), and only connects once the
 * parent answers the `ready` handshake with `init`. Once connected it renders the editor's
 * working draft code (including unsaved Monaco edits) instead of the served
 * draft, and hosts design mode inside the preview document, relaying its
 * edits and AI handoffs to the editor (whose autosave persists the edits).
 *
 * `routeParams` is the concrete location this document reached, or null when its
 * URL is not the page's authored route. It is re-announced on its own — moving
 * between two values of the same page is a navigation the editor has to adopt,
 * but it is not a different page, so the draft override must survive it.
 */
export function useEditorPreviewBridge(
  pageId: string,
  viewer: EditorPreviewViewer | null,
  routeParams: Record<string, string> | null
): {
  isConnected: boolean;
  code: string | null;
  designMode: PageDesignModeProp | undefined;
} {
  const [isConnected, setIsConnected] = useState(false);
  const [codeOverride, setCodeOverride] = useState<string | null>(null);
  const [isDesignModeEnabled, setIsDesignModeEnabled] = useState(false);
  // Whether the editor is showing this frame. Not reset on an in-preview
  // navigation: the view the author is on does not change with the page.
  const [isVisible, setIsVisible] = useState(true);
  const [canOpenImportedSources, setCanOpenImportedSources] = useState(true);
  const pendingImagePicksRef = useRef(new Map<string, (fileId: string | null) => void>());
  const viewerId = viewer?.id;
  const viewerResourceId = viewer?.resourceId;
  const viewerDisplayName = viewer?.displayName;

  // An in-preview Inertia navigation swaps the page props without remounting
  // this component, so the previous page's draft override must not bleed into
  // the page navigated to. Reset and re-handshake (the mount effect below
  // re-runs on the pageId change and announces the new page to the editor).
  const [prevPageId, setPrevPageId] = useState(pageId);
  if (pageId !== prevPageId) {
    setPrevPageId(pageId);
    setIsConnected(false);
    setCodeOverride(null);
    setIsDesignModeEnabled(false);
  }

  const isEmbedded = isInsideEditorPreviewFrame();
  const currentRouteParamsKey = routeParams === null ? null : routeParamsKey(routeParams);

  const announceReady = useEffectEvent(() => {
    sendToEditor(pageId, {
      event: 'ready',
      viewer:
        viewerId !== undefined && viewerResourceId !== undefined && viewerDisplayName !== undefined
          ? {
              id: viewerId,
              resourceId: viewerResourceId,
              displayName: viewerDisplayName
            }
          : null,
      routeParams
    });
  });

  useEffect(() => {
    if (!isEmbedded) {
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.source !== window.parent) {
        return;
      }

      if (!isEditorPreviewMessage(event.data) || event.data.pageId !== pageId) {
        return;
      }

      const message = event.data as EditorPreviewParentMessage;

      switch (message.event) {
        case 'init':
          setIsConnected(true);
          setCodeOverride(message.code);
          setIsDesignModeEnabled(message.isDesignModeEnabled);
          setThemeOverride(message.theme ?? null);
          setIsVisible(message.isVisible ?? true);
          setCanOpenImportedSources(message.canOpenImportedSources ?? true);
          break;
        case 'set-code':
          setCodeOverride(message.code);
          break;
        case 'set-design-mode':
          setIsDesignModeEnabled(message.isEnabled);
          break;
        case 'set-theme':
          setThemeOverride(message.theme);
          break;
        case 'set-visible':
          setIsVisible(message.isVisible);
          break;
        case 'image-picked': {
          const resolvePick = pendingImagePicksRef.current.get(message.requestId);
          pendingImagePicksRef.current.delete(message.requestId);
          resolvePick?.(message.fileId);
          break;
        }
      }
    };

    window.addEventListener('message', handleMessage);

    const pendingImagePicks = pendingImagePicksRef.current;

    return () => {
      window.removeEventListener('message', handleMessage);
      // A navigation away re-runs this effect for another page; nothing will
      // answer requests made by the previous document, so settle them.
      for (const resolvePick of pendingImagePicks.values()) {
        resolvePick(null);
      }
      pendingImagePicks.clear();
    };
  }, [isEmbedded, pageId]);

  // Announced separately from the listener above so a param-only navigation —
  // the same page at another route value — re-reports the location without
  // tearing down the connection or the draft code the editor pushed.
  useEffect(() => {
    if (!isEmbedded) {
      return;
    }

    announceReady();
  }, [isEmbedded, pageId, currentRouteParamsKey, viewerDisplayName, viewerId, viewerResourceId]);

  // A hidden document holds its navigations. The editor follows every page
  // and route the preview reaches, so a redirect the page scheduled would
  // otherwise move the editor while the author works in Code or Settings.
  // Cancelling at the router covers `Sdk.router`, `Sdk.auth` and Inertia
  // links alike; the visit is dropped, not queued, so returning to Preview
  // shows the document the author left.
  useEffect(() => {
    if (!isEmbedded || isVisible) {
      return;
    }

    const stopHoldingNavigations = router.on('before', () => false);

    return () => stopHoldingNavigations();
  }, [isEmbedded, isVisible]);

  const pickImage = () =>
    new Promise<string | null>((resolve) => {
      const requestId = randomUuid();

      pendingImagePicksRef.current.set(requestId, resolve);
      sendToEditor(pageId, { event: 'pick-image', requestId });
    });

  // Design mode writes its selection and Ask-AI requests into this document's
  // handoff store, but the chat panel that consumes them lives in the editor —
  // forward both across the frame boundary.
  useEffect(() => {
    if (!isConnected) {
      return;
    }

    let lastSelectionContext = pageAssistantHandoffStore.getSnapshot().selectionContext;
    let lastAskAiRequestId: string | null = null;

    const unsubscribe = pageAssistantHandoffStore.subscribe(() => {
      const snapshot = pageAssistantHandoffStore.getSnapshot();

      if (snapshot.selectionContext !== lastSelectionContext) {
        lastSelectionContext = snapshot.selectionContext;
        sendToEditor(pageId, {
          event: 'selection-change',
          selectionContext: snapshot.selectionContext
        });
      }

      const askAiRequest = snapshot.askAiRequest;

      if (askAiRequest && askAiRequest.id !== lastAskAiRequestId) {
        lastAskAiRequestId = askAiRequest.id;
        sendToEditor(pageId, {
          event: 'ask-ai',
          prompt: askAiRequest.prompt,
          attachments: askAiRequest.attachments ?? []
        });
        pageAssistantHandoffStore.consumeAssistantEditRequest(askAiRequest.id);
      }
    });

    return unsubscribe;
  }, [isConnected, pageId]);

  const designMode: PageDesignModeProp | undefined = isConnected
    ? {
        isAvailable: true,
        enabled: isDesignModeEnabled,
        onEnabledChange: (enabled: boolean) => {
          setIsDesignModeEnabled(enabled);
          sendToEditor(pageId, {
            event: 'design-mode-change',
            isEnabled: enabled
          });
        },
        onCodeChange: (code: string) => {
          setCodeOverride(code);
          sendToEditor(pageId, { event: 'code-change', code });
        },
        pickImage,
        canOpenImportedSources
      }
    : undefined;

  return {
    isConnected,
    code: isConnected ? codeOverride : null,
    designMode
  };
}
